import styled from '@emotion/styled';
import { Segmented, Tabs, type TabsProps, theme } from 'antd';
import { type CarouselRef } from 'antd/lib/carousel';
import React, { Children, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';

const StyledSegmented = styled(Segmented<string>)`
  & .ant-segmented-item-label {
    padding: calc(var(--ant-padding-xxs) - var(--ant-line-width))
      calc(var(--ant-control-padding-horizontal) - var(--ant-line-width)) !important;
  }
  & .ant-segmented-group {
    gap: 3px;
  }
`;

export interface SegmentedTabsProps {
  children: React.ReactElement<TabPaneProps> | Array<React.ReactElement<TabPaneProps>>;
  initialActiveKey?: string;
  activeKey?: string;
  onChange?: (key: string) => void;
}

export interface TabPaneProps {
  children: ReactNode;
  label: string;
  value: string;
  disabled?: boolean;
  hidden?: boolean;
}

export const TabPane = (props: TabPaneProps): ReactNode => {
  return props.children;
};

export const SegmentedTabs = ({
  children,
  initialActiveKey,
  activeKey,
  onChange,
}: SegmentedTabsProps): ReactNode => {
  const { token } = theme.useToken();
  const items = useMemo(
    () =>
      Children.map(
        children,
        (item) =>
          Object.assign({}, item.props, {
            key: item.props.value,
            children: (
              <div style={{ minHeight: 800, padding: token.paddingMD }}>{item.props.children}</div>
            ),
          }) as TabPaneProps & { key: string; style: React.CSSProperties }
      ).filter((item) => !item.hidden),
    [children]
  );
  const [activeTabKey, setActiveTabKey] = useState<string>(initialActiveKey ?? items[0].value);
  const carouselRef = useRef<CarouselRef>(null);

  useEffect(() => {
    const itemIdx = items.findIndex((item) => item.value === activeTabKey);
    carouselRef.current?.goTo(itemIdx);
    onChange?.(activeTabKey);
  }, [activeTabKey]);

  useEffect(() => {
    setActiveTabKey(activeKey ?? activeTabKey);
  }, [activeKey]);

  const renderTabBar: TabsProps['renderTabBar'] = ({ style, ...props }: any) => (
    <StickyBox offsetTop={0} offsetBottom={0} style={{ zIndex: 2 }}>
      <StyledSegmented
        block
        options={items}
        value={activeTabKey ?? undefined}
        style={{
          marginBlockEnd: token.marginXS,
          marginBlockStart: token.marginXXS,
          marginInline: token.marginXXS,
        }}
        onChange={setActiveTabKey}
        {...props}
      />
    </StickyBox>
  );

  return (
    <Tabs
      items={items}
      defaultActiveKey={items[0].value}
      renderTabBar={renderTabBar}
      activeKey={activeTabKey}
    />
  );
};

SegmentedTabs.TabPane = TabPane;
